import { faCircleInfo, faLock } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Box from "@mui/material/Box"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"

import { IComplaintSummary } from "pages/Complaints/types/complaintLogistics"
import { convertSnakeCaseToCapitalizeCase } from "utils/utils"

/**
 * A field whose value is fixed by the context the form was opened in.
 *
 * Rendered as a read-only value with a lock glyph rather than a disabled dropdown on purpose: a
 * greyed-out caret still looks clickable, so users try it, nothing happens, and it gets reported
 * as a bug. A locked row with a reason reads as intentional.
 */
export const LockedValueField = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
    <Box display="flex" flexDirection="column" gap={1}>
        <Typography variant="label">{label}*</Typography>
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
            bgcolor="#EEF0F5"
            borderRadius="4px 4px 0 0"
            borderBottom="1px solid #E4E7EE"
            padding="11px 12px"
            minHeight="42px">
            {/* 500 rather than 600: this is a read-only value, so it should not out-weigh the
                editable fields around it. Set through sx, since the theme's MuiTypography
                styleOverrides can beat the fontSize/fontWeight props. */}
            <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "statiqBlue.main" }}>
                {value}
            </Typography>
            <FontAwesomeIcon icon={faLock} color="#A3ABBB" size="sm" />
        </Box>
        {hint && (
            <Typography fontSize="12px" color="#5B6982">
                {hint}
            </Typography>
        )}
    </Box>
)

/**
 * Complaint status chip colours, taken from the design's tag palette.
 *
 * Resolved gets its own green so it reads as a finished complaint at a glance — it is the status
 * that changes what the person raising the dispatch should think about (the part is shipping after
 * resolution), so it should not look like the in-progress amber.
 */
const STATUS_TONES: Record<string, { color: string; bgcolor: string; border: string }> = {
    RESOLVED: { color: "#1F9D63", bgcolor: "#E6F6ED", border: "1px solid #C6EAD6" },
    IN_PROGRESS: { color: "#B4761A", bgcolor: "#FDF3E3", border: "1px solid #F3E0BD" },
    DEFERRED: { color: "#D8452F", bgcolor: "#FFEEEC", border: "1px solid #FFD5CF" },
    UNDER_OBSERVATION: { color: "#2D6CDF", bgcolor: "#EAF1FD", border: "1px solid #CFE0FA" },
    OPEN: { color: "#5B6982", bgcolor: "#F4F6F9", border: "1px solid #E4E7EE" },
}

/** Unknown or new statuses fall back to neutral rather than borrowing another status's meaning. */
const NEUTRAL_TONE = STATUS_TONES.OPEN

const statusTone = (status: string) => STATUS_TONES[status?.toUpperCase()] ?? NEUTRAL_TONE

const StatusChip = ({ status }: { status: string }) => (
    <Box
        fontSize="11.5px"
        fontWeight={600}
        padding="3px 9px"
        borderRadius="4px"
        whiteSpace="nowrap"
        {...statusTone(status)}>
        {convertSnakeCaseToCapitalizeCase(status)}
    </Box>
)

/** The time portion of a timestamp, whether separated by a space or an ISO "T". */
const TIME_PORTION = /[\sT]\d{1,2}:\d{2}.*$/

/**
 * When the complaint was raised — the date shown exactly as the API wrote it, in IST.
 *
 * No Date parsing at all, deliberately. `created_at` arrives as "Tue, 08 Sep 2026 11:17:52 IST",
 * and `new Date()` rejects "IST" (it knows only GMT, UT, Z and the US abbreviations), so anything
 * that parses first ends up with an Invalid Date and no date on the card. Trimming the time off
 * the string sidesteps that, and cannot shift the day into another timezone the way a conversion
 * would.
 */
const formatRaisedOn = (value?: string | null): string | null => {
    const date = value?.replace(TIME_PORTION, "").trim()
    return date ? `Raised ${date}` : null
}

/**
 * Who owns the complaint, from `stati_engineer_name` and `department`.
 *
 * Shown only when both are present: an engineer without a department, or a department with no
 * engineer named, is a half-answer that reads as though the other half were missing on purpose.
 */
const formatOwner = (complaint: IComplaintSummary): string | null => {
    const name = complaint.owner_name?.trim()
    const department = complaint.owner_department?.trim()
    if (!name || !department) return null
    return `Owner: ${name} (${department})`
}

/**
 * Read-only confirmation aid shown once a complaint is attached — it exists so whoever raises the
 * dispatch can see they picked the right ticket before moving on, since the reference cannot be
 * corrected after the ticket is created.
 */
export const ComplaintSummaryCard = ({
    complaint,
    existingDispatchCount = 0,
}: {
    complaint: IComplaintSummary
    /**
     * Dispatches already raised against this complaint. Surfaced as an icon on the heading rather
     * than its own banner: raising another is allowed — a second part, or a re-dispatch after a
     * failed delivery — so it is a heads-up about the complaint in hand, not a page-level warning.
     */
    existingDispatchCount?: number
}) => {
    const subTypeLabel = complaint.complaint_sub_type || complaint.complaint_type
    const meta = [
        complaint.site_name,
        complaint.charger_id,
        formatRaisedOn(complaint.created_at),
        formatOwner(complaint),
    ]
        .filter(Boolean)
        .join(" · ")

    const existingDispatchNote =
        existingDispatchCount === 1
            ? "1 dispatch is already linked to this complaint."
            : `${existingDispatchCount} dispatches are already linked to this complaint.`

    return (
        <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" alignItems="center" gap="6px">
                <Typography variant="label">Complaint summary</Typography>
                {existingDispatchCount > 0 && (
                    <Tooltip title={existingDispatchNote} placement="top">
                        {/* span wrapper: MUI needs a real element to hang the tooltip on, and the
                            icon is focusable so the note is reachable without a pointer. */}
                        <Box component="span" display="inline-flex" tabIndex={0} sx={{ cursor: "help" }}>
                            {/* #A8A8A8 is the info-icon grey the other ProjectForm steps use. */}
                            <FontAwesomeIcon icon={faCircleInfo} color="#A8A8A8" size="sm" />
                        </Box>
                    </Tooltip>
                )}
            </Box>
            <Box border="1px solid #E4E7EE" borderRadius="8px" padding="12px 14px">
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                    <Typography fontSize="14px" fontWeight={500}>
                        {complaint.display_id}
                        {subTypeLabel ? ` · ${subTypeLabel}` : ""}
                    </Typography>
                    {complaint.status && <StatusChip status={complaint.status} />}
                </Box>
                {meta && (
                    <Typography variant="label" color="#5B6982" marginTop="4px">
                        {meta}
                    </Typography>
                )}
            </Box>
        </Box>
    )
}
